import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../../hooks/redux';
import { fetchRequirementStepsByAISystemId } from '../../../store/slices/requirementStepsSlice';
import { RequirementListStep } from './RequirementListStep';
import { SidePanel } from '../../ui/SidePanel/SidePanel';
import { TextButton } from '../../ui/Button/Buttons';
import {
  handleOpenSidePanel,
  setSidePanelContent,
  setSidePanelTitle
} from '../../../store/slices/sidePanelSlice';
import {
  setAnnexIVFieldsState,
  setCreateAnnexIVDraftResponse
} from '../../../store/slices/documentationSlice';
import { getAnnexIVByAiSystem } from '../../../services/AnnexIVService';
import { mapAnnexContentToFields } from '../../../utils/documentationHelpers';
import {
  getStepOrdering,
  STEPS_DESCRIPTION
} from '../../../utils/RequirementSteps.util';

export function AISysRequirementList() {
  const dispatch = useAppDispatch();
  const { id: aiSystemId } = useParams<{ id: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const hasOpenedAnnexDrawerRef = useRef(false);
  const { steps, loading, error } = useAppSelector(
    (state) => state.requirementSteps
  );
  const {
    createAnnexIVDraftResponse,
    createEUDeclarationResponse,
    createFRIAResponse,
    createDPIAResponse
  } = useAppSelector((state) => state.documentation);

  const { sidePanelTitle, sidePanelContent } = useAppSelector(
    (state) => state.sidePanel
  );

  const [showOtherObligations, setShowOtherObligations] = useState(false);

  // The pack's own unlock metadata decides whether this list is a sequence at
  // all, and `unknown` makes no claim rather than defaulting to "sequential".
  //
  // Steps are never cleared on pending/rejected (the slice keeps the previous
  // system's list), so a system switch or a failed refetch would otherwise
  // render a verdict about the WRONG pack. While loading or errored we know
  // nothing, and `unknown` says exactly that.
  const ordering = getStepOrdering(loading || error ? [] : steps);
  const stepsDescription = STEPS_DESCRIPTION[ordering];

  useEffect(() => {
    if (aiSystemId) {
      dispatch(fetchRequirementStepsByAISystemId(aiSystemId));
    }
  }, [dispatch, aiSystemId]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const shouldOpenAnnexDrawer = params.get('openAnnexIVDrawer') === '1';

    if (
      !aiSystemId ||
      !shouldOpenAnnexDrawer ||
      hasOpenedAnnexDrawerRef.current
    ) {
      return;
    }

    hasOpenedAnnexDrawerRef.current = true;

    const openAnnexDrawer = async () => {
      try {
        const annex = await getAnnexIVByAiSystem(aiSystemId);
        dispatch(setCreateAnnexIVDraftResponse(annex));
        dispatch(setAnnexIVFieldsState(mapAnnexContentToFields(annex)));
        dispatch(setSidePanelTitle('Annex IV - Technical Documentation (Draft)'));
        dispatch(setSidePanelContent('ANNEX_IV'));
        dispatch(handleOpenSidePanel(true));
      } catch (error) {
        console.error('Failed to open Annex IV side drawer:', error);
      } finally {
        params.delete('openAnnexIVDrawer');
        navigate(
          {
            pathname: location.pathname,
            search: params.toString() ? `?${params.toString()}` : ''
          },
          { replace: true }
        );
      }
    };

    void openAnnexDrawer();
  }, [aiSystemId, dispatch, location.pathname, location.search, navigate]);

  const handleContinue = (stepId: string) => {
    console.log('Continue work on step:', stepId);
    // Handle navigation or action for the step
  };

  // One header for all three branches: duplicating it is how the sequential
  // copy leaked into the loading and error states in the first place.
  const header = (
    <div className="flex flex-col text-left">
      <span className="text-zinc-900 font-medium">Requirements</span>
      {stepsDescription && (
        <span className="text-gray-500 text-sm font-normal">
          {stepsDescription}
        </span>
      )}
    </div>
  );

  if (loading) {
    return (
      <div className="flex flex-col gap-6 max-h-[calc(100vh-300px)] overflow-y-auto">
        {header}
        <div className="flex items-center justify-center py-8">
          <span className="text-gray-500">Loading requirement steps...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col gap-6 max-h-[calc(100vh-300px)] overflow-y-auto">
        {header}
        <div className="flex items-center justify-center py-8">
          <span className="text-red-600">Error: {error}</span>
        </div>
      </div>
    );
  }

  const isImporter = steps.some((step) => step.key?.startsWith('IMPORTER_'));
  const isDistributor = steps.some((step) =>
    step.key?.startsWith('DISTRIBUTOR_')
  );

  return (
    <div className="flex flex-col gap-6 max-h-[calc(100vh-300px)] overflow-y-auto">
      {header}

      <div className="flex flex-col gap-2">
        {steps.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            No requirement steps available
          </div>
        ) : (
          steps.map((step) => (
            <RequirementListStep
              key={step.id}
              stepKey={step.key}
              stepId={step.id}
              order={step.order}
              title={step.title}
              status={step.status}
              overview={step.overview}
              locked={step.locked}
              orderless={ordering === 'parallel'}
              expiresAt={step.expiresAt}
              renewableAt={step.renewableAt}
              leadTime={step.leadTime}
              leadTimeAcknowledgedAt={step.leadTimeAcknowledgedAt}
              onContinue={() => handleContinue(step.id)}
            />
          ))
        )}
      </div>

      {(isImporter || isDistributor) && (
        <div className="flex flex-col gap-3 items-start">
          <TextButton
            onClick={() => setShowOtherObligations(!showOtherObligations)}
            className="text-xs"
          >
            {showOtherObligations
              ? 'Hide further obligations'
              : 'See further obligations'}
          </TextButton>
          {showOtherObligations && isImporter && (
            <div className="bg-gray-50 rounded-lg p-4 text-left text-xs leading-relaxed text-gray-600">
              <p>
                <span className="font-medium text-gray-700">Note:</span> This
                checklist covers the pre-market verification, identity and
                record-keeping duties under Article 23. As an importer you have
                further obligations that are not tracked here:
              </p>
              <ul className="list-disc list-outside pl-4 mt-2 space-y-1">
                <li>
                  Not placing a non-conforming or falsified system on the
                  market and, where it presents a risk, informing the provider,
                  the authorised representative and the market surveillance
                  authorities (Art. 23(2)).
                </li>
                <li>
                  Ensuring storage and transport conditions do not compromise
                  compliance (Art. 23(4)).
                </li>
                <li>
                  Providing a competent authority with information on request
                  (Art. 23(6)).
                </li>
                <li>
                  Cooperating with authorities to mitigate risk (Art. 23(7)).
                </li>
              </ul>
              <p className="mt-2">
                See{' '}
                <a
                  href="https://eur-lex.europa.eu/eli/reg/2024/1689/oj/eng#art_23"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-purple-800 underline"
                >
                  Article 23
                </a>
                .
              </p>
            </div>
          )}
          {showOtherObligations && isDistributor && (
            <div className="bg-gray-50 rounded-lg p-4 text-left text-xs leading-relaxed text-gray-600">
              <p>
                <span className="font-medium text-gray-700">Note:</span> This
                checklist covers the pre-market verification duties under
                Article 24(1) — confirming the CE marking, the EU declaration of
                conformity and instructions, and that the provider and importer
                met their obligations. As a distributor you have further
                obligations that are not tracked here:
              </p>
              <ul className="list-disc list-outside pl-4 mt-2 space-y-1">
                <li>
                  Not making a system available while you consider it
                  non-conforming and, where it presents a risk, informing the
                  provider or importer, as applicable (Art. 24(2)).
                </li>
                <li>
                  Ensuring storage and transport conditions do not jeopardise
                  compliance (Art. 24(3)).
                </li>
                <li>
                  Taking the necessary corrective actions — bringing it into
                  conformity, withdrawing it, or recalling it — and, where it
                  presents a risk, immediately informing the provider or importer
                  and the competent authorities (Art. 24(4)).
                </li>
                <li>
                  Providing a competent authority with information on request
                  (Art. 24(5)).
                </li>
                <li>
                  Cooperating with authorities to mitigate risk (Art. 24(6)).
                </li>
              </ul>
              <p className="mt-2">
                See{' '}
                <a
                  href="https://eur-lex.europa.eu/eli/reg/2024/1689/oj/eng#art_24"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-purple-800 underline"
                >
                  Article 24
                </a>
                .
              </p>
            </div>
          )}
        </div>
      )}

      {sidePanelContent && (
        <SidePanel
          title={sidePanelTitle}
          content={sidePanelContent}
          annexId={
            sidePanelContent === 'ANNEX_IV'
              ? createAnnexIVDraftResponse?.id
              : undefined
          }
          declarationId={
            sidePanelContent === 'EU_DECLARATION'
              ? createEUDeclarationResponse?.id
              : undefined
          }
          dpiaId={
            sidePanelContent === 'DPIA' ? createDPIAResponse?.id : undefined
          }
          friaId={
            sidePanelContent === 'FRIA' ? createFRIAResponse?.id : undefined
          }
        />
      )}
    </div>
  );
}
